import React from 'react';
import { ButtonLoadingSpinner } from './LoadingSpinner';
import { useDesignTokens } from '../hooks/useTheme';
import { usePortalAccent } from '../hooks/usePortalAccent';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button visual style variant
   * - primary: Main action buttons (sky)
   * - secondary: Secondary actions (gray)
   * - danger: Destructive actions (red)
   * - success: Success actions (green)
   * - ghost: Transparent background, subtle hover
   * - outline: Bordered style
   */
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'ghost' | 'outline';

  /**
   * Button size
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';

  /**
   * Show loading spinner and disable button
   */
  loading?: boolean;

  /**
   * Icon to display in the button
   */
  icon?: React.ComponentType<{ size?: string | number; className?: string }>;

  /**
   * Position of the icon relative to text
   */
  iconPosition?: 'left' | 'right';

  /**
   * Make the button full width
   */
  fullWidth?: boolean;

  /**
   * Children content (button text/content)
   */
  children?: React.ReactNode;
}

/**
 * Standardized Button component with native HTML button
 * with consistent styling, loading states, and icon handling
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const tokens = useDesignTokens();
  const accent = usePortalAccent();

  // Primary follows the surrounding portal accent (sky in the user app, amber
  // in the admin portal). Full literal class strings so Tailwind keeps them.
  const primaryStyle =
    accent === 'amber'
      ? 'bg-amber-600 hover:bg-amber-700 active:bg-amber-800 focus:ring-amber-200 dark:focus:ring-amber-900 text-white border-amber-600'
      : 'bg-sky-600 hover:bg-sky-700 active:bg-sky-800 focus:ring-sky-200 dark:focus:ring-sky-900 text-white border-sky-600';

  // Define variant styles with proper dark mode support
  const variantStyles = {
    primary: primaryStyle,
    secondary:
      'bg-zinc-600 hover:bg-zinc-700 active:bg-zinc-800 focus:ring-zinc-200 dark:focus:ring-zinc-700 text-white border-zinc-600 dark:bg-zinc-500 dark:hover:bg-zinc-600 dark:active:bg-zinc-700',
    danger:
      'bg-red-600 hover:bg-red-700 active:bg-red-800 focus:ring-red-200 dark:focus:ring-red-900 text-white border-red-600',
    success:
      'bg-green-600 hover:bg-green-700 active:bg-green-800 focus:ring-green-200 dark:focus:ring-green-900 text-white border-green-600',
    ghost:
      'bg-transparent hover:bg-zinc-100 active:bg-zinc-200 dark:hover:bg-zinc-700 dark:active:bg-zinc-600 text-zinc-700 dark:text-zinc-300 border-transparent',
    outline:
      'bg-transparent hover:bg-zinc-50 active:bg-zinc-100 dark:hover:bg-zinc-700 dark:active:bg-zinc-600 text-zinc-700 dark:text-zinc-300 border border-zinc-300 dark:border-zinc-600',
  };

  // Size configurations
  const sizeConfig = {
    xs: { className: 'px-2 py-1 text-xs', iconSize: 12 },
    sm: { className: 'px-3 py-2 text-sm', iconSize: 14 },
    md: { className: 'px-4 py-2 text-base', iconSize: 16 },
    lg: { className: 'px-6 py-3 text-lg', iconSize: 18 },
  };

  const currentSizeConfig = sizeConfig[size];
  const isDisabled = disabled || loading;

  // Build the button classes
  const buttonClasses = [
    // Base styles
    'inline-flex items-center justify-center font-medium rounded-lg focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed select-none',
    // Variant styles
    variantStyles[variant],
    // Size styles
    currentSizeConfig.className,
    // Transitions
    tokens.transitions.colors,
    // Width
    fullWidth ? 'w-full' : '',
    // Custom classes
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconElement = Icon && (
    <Icon
      size={currentSizeConfig.iconSize}
      className={(() => {
        if (children === null || children === undefined) {
          return '';
        }
        return iconPosition === 'left' ? 'mr-2' : 'ml-2';
      })()}
    />
  );

  return (
    <button {...props} type={type} className={buttonClasses} disabled={isDisabled}>
      {loading && (
        <ButtonLoadingSpinner
          className={children !== null && children !== undefined ? 'mr-2' : ''}
        />
      )}
      {!loading && Boolean(Icon) && iconPosition === 'left' && iconElement}
      {children}
      {!loading && Boolean(Icon) && iconPosition === 'right' && iconElement}
    </button>
  );
}

/**
 * Convenience components for common button patterns
 */
export const PrimaryButton = (props: Omit<ButtonProps, 'variant'>) => (
  <Button {...props} variant="primary" />
);

export const SecondaryButton = (props: Omit<ButtonProps, 'variant'>) => (
  <Button {...props} variant="secondary" />
);

export const DangerButton = (props: Omit<ButtonProps, 'variant'>) => (
  <Button {...props} variant="danger" />
);

export const SuccessButton = (props: Omit<ButtonProps, 'variant'>) => (
  <Button {...props} variant="success" />
);

export const GhostButton = (props: Omit<ButtonProps, 'variant'>) => (
  <Button {...props} variant="ghost" />
);

export const OutlineButton = (props: Omit<ButtonProps, 'variant'>) => (
  <Button {...props} variant="outline" />
);

// Default export for easier imports
export default Button;
